import { useEffect, useState } from 'react'
import { api } from '../../../api'
import VisuelProduit from './VisuelProduit'
import { ORANGE, TEAL, VERT, eur, fmtDate } from './paniersProd'
import { avecAlpha } from '../../../lib/couleurs'

/**
 * Onglet « Mes offres » du module Paniers producteur (retours Enzo 12/07,
 * v7.3 palier 2) : DEUX SECTIONS explicites — « Proposées en Paniers »
 * (bouton « Retirer des Paniers », engagements 🚚 Sur : …) et « Pas encore en
 * Paniers » (« + Proposer en Paniers »). Pilote par l'offre generale (toggle
 * canal TOURNEE partage) — la tournee ne cree jamais d'offre : le wizard vit
 * dans Mes offres (hub). Retrait en CASCADE sans reservation ; avec
 * reservations, le backend bloque (demande d'ajustement = palier 3).
 */

const Section = ({ titre, children }) => (
  <>
    <p className="text-[11px] font-bold uppercase mb-2 mt-4 mx-0.5 first:mt-0" style={{ letterSpacing: 2, color: TEAL }}>
      {titre}
    </p>
    {children}
  </>
)

function CarteOffre({ o, busy, onToggle }) {
  return (
    <div className="glass-surface rounded-[18px]" style={{ padding: '13px 15px' }}>
      <div className="flex items-center gap-3">
        <VisuelProduit photo={o.photo} emoji={o.emoji} taille={44} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate" style={{ color: VERT }}>{o.produit}</p>
          <p className="text-[11px] text-ink/50">
            {eur(o.prix)} / {o.unite}
            {o.fluxTendu ? ' · flux tendu' : o.stock != null ? ` · stock ${o.stock}` : ''}
          </p>
        </div>
        {o.enPaniers ? (
          o.canalPrincipal ? (
            <span className="text-[10px] font-extrabold rounded-full flex-shrink-0" style={{ padding: '5px 10px', background: 'rgba(20,184,166,0.12)', color: '#0d6b5f' }}>
              Canal principal
            </span>
          ) : (
            <button
              onClick={() => onToggle(o, false)}
              disabled={busy}
              className="rounded-full text-[11px] font-extrabold flex-shrink-0 disabled:opacity-40"
              style={{ padding: '7px 13px', border: '1px solid rgba(7,62,61,0.18)', background: 'rgba(255,255,255,0.4)', color: 'rgba(7,62,61,0.65)' }}
            >
              Retirer des Paniers
            </button>
          )
        ) : (
          <button
            onClick={() => onToggle(o, true)}
            disabled={busy}
            className="rounded-full text-[11.5px] font-extrabold flex-shrink-0 disabled:opacity-40"
            style={{ padding: '7px 13px', border: 'none', background: TEAL, color: '#04302f' }}
          >
            + Proposer en Paniers
          </button>
        )}
      </div>
      {o.enPaniers && o.engagements.length > 0 && (
        <p className="text-[10.5px] mt-2 leading-relaxed" style={{ color: 'rgba(7,62,61,0.55)' }}>
          🚚 Sur : {o.engagements.map((e) => `${e.nom || `tournée du ${fmtDate(e.dateLivraison)}`} (${e.vendu > 0 ? `${e.vendu} réservés / ` : ''}${e.dispo} ${o.unite})`).join(' · ')}
        </p>
      )}
    </div>
  )
}

function MesOffresPaniers({ onToast, onGoOffers }) {
  const [offres, setOffres] = useState(null)
  const [busy, setBusy] = useState(false)

  const charger = () => api.producer.getPaniersOffres().then(setOffres).catch(() => setOffres([]))
  useEffect(() => { charger() }, [])

  const toggle = (o, actif) => {
    setBusy(true)
    api.producer.setOfferChannel(o.productOfferId, 'TOURNEE', actif)
      .then(() => charger())
      .then(() => {
        if (!actif) onToast?.(`${o.produit} retirée des Paniers${o.engagements.length ? ' — et des tournées en cours' : ''}.`)
      })
      .catch((e) => onToast?.(e.message || 'La modification a échoué.'))
      .finally(() => setBusy(false))
  }

  if (!offres) return <div className="h-40 rounded-3xl animate-pulse bg-ink/5" />

  const proposees = offres.filter((o) => o.enPaniers)
  const restantes = offres.filter((o) => !o.enPaniers)

  return (
    <>
      <Section titre={`Proposées en Paniers (${proposees.length})`}>
        {proposees.length === 0 ? (
          <div className="glass-surface rounded-3xl p-5 text-center">
            <p className="text-[13px] text-ink/65">Aucune offre en Paniers pour l'instant.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {proposees.map((o) => <CarteOffre key={o.productOfferId} o={o} busy={busy} onToggle={toggle} />)}
          </div>
        )}
      </Section>

      <Section titre={`Pas encore en Paniers (${restantes.length})`}>
        {restantes.length === 0 ? (
          <div className="glass-surface rounded-3xl p-5 text-center">
            <p className="text-[13px] text-ink/65">Toutes vos offres actives sont déjà en Paniers.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {restantes.map((o) => <CarteOffre key={o.productOfferId} o={o} busy={busy} onToggle={toggle} />)}
          </div>
        )}
      </Section>

      <button
        onClick={onGoOffers}
        className="w-full mt-4 rounded-2xl text-[13px] font-extrabold"
        style={{ padding: 13, border: `1.5px solid ${avecAlpha(ORANGE, 0.4)}`, background: 'rgba(244,137,83,0.07)', color: ORANGE }}
      >
        📦 Créer ou modifier une offre (Mes offres)
      </button>
      <p className="text-[10.5px] leading-relaxed mt-2 mx-0.5 text-ink/45">
        🦊 Un seul chemin pour créer une offre : le wizard de Mes offres. Ici, vous choisissez ce qui est proposé en Paniers — mêmes données, un seul stock.
      </p>
    </>
  )
}

export default MesOffresPaniers
